import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, Link2, Sparkles, ArrowLeft, ArrowRight,
  X, Check, Loader2, Plus, Minus, Globe, Pencil,
  Image, Type, Settings2, MapPin, Search, Copy, User, Upload, FileText, ChevronDown,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import StepPersonFinalize from '../onboarding/StepPersonFinalize';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const PLATFORMS = ['LinkedIn', 'Instagram', 'Facebook', 'X (Twitter)', 'Blog', 'Newsletter', 'Pinterest'];

const anim = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -16 }, transition: { duration: 0.2 } };

export const NewProjectOnboarding = ({ onComplete, onCancel }) => {
  const { getAuthHeader, setActiveProjectId } = useAuth();
  const { lang } = useLanguage();
  const mounted = useRef(true);

  const RELATIONSHIPS = [
    { id: 'same_brand', icon: Building2, title: lang === 'de' ? 'Gleiche Marke, anderer Voice' : 'Same Brand, Different Voice', desc: lang === 'de' ? 'z.B. CEO Personal Brand, Tochtermarke' : 'e.g. CEO Personal Brand, Sub-brand', detail: lang === 'de' ? 'Visuelles Brand DNA wird kopiert, du definierst nur Text-Stimme & Plattformregeln neu.' : 'Visual Brand DNA is copied — you only redefine tone of voice & platform rules.' },
    { id: 'similar_brand', icon: Link2, title: lang === 'de' ? 'Ähnliche Marke' : 'Similar Brand', desc: lang === 'de' ? 'z.B. zweites Produkt, Schwesterunternehmen' : 'e.g. second product, sister company', detail: lang === 'de' ? 'Wähle welche Brand DNA-Elemente kopiert werden sollen.' : 'Choose which Brand DNA elements to copy.' },
    { id: 'independent', icon: Sparkles, title: lang === 'de' ? 'Komplett unabhängig' : 'Completely independent', desc: lang === 'de' ? 'z.B. neuer Klient' : 'e.g. new client', detail: lang === 'de' ? 'Volles Onboarding von Grund auf.' : 'Full onboarding from scratch.' },
  ];

  const CLONE_ELEMENTS = [
    { id: 'brand_media', label: lang === 'de' ? 'Logo & Farben (Bildsprache)' : 'Logo & Colors (Visual Style)', icon: Image, setupStep: 'setup_media' },
    { id: 'writing_style', label: lang === 'de' ? 'Schreibstil & Brand Manual' : 'Writing Style & Brand Manual', icon: Type, setupStep: 'setup_writing' },
    { id: 'channel_settings', label: lang === 'de' ? 'Plattformregeln & Limits' : 'Platform Rules & Limits', icon: Settings2, setupStep: 'setup_channels' },
    { id: 'brand_geo_settings', label: lang === 'de' ? 'GEO-Einstellungen' : 'GEO Settings', icon: MapPin, setupStep: 'setup_geo' },
    { id: 'brand_seo_settings', label: lang === 'de' ? 'SEO-Einstellungen' : 'SEO Settings', icon: Search, setupStep: 'setup_seo' },
    { id: 'brand_inspirations', label: lang === 'de' ? 'Inspiration-Beispiele' : 'Inspiration Examples', icon: Copy, setupStep: 'setup_inspirations' },
  ];

  const [step, setStep] = useState('type');
  const [projectType, setProjectType] = useState('company');

  // ── Persönliche Marke (3 Schritte: Name · Lebenslauf · Arbeitsumfeld) ──
  const [pbBasics, setPbBasics] = useState({ name: '', gender: null });   // gender: female | male | diverse | none
  const [pbError, setPbError] = useState('');
  const [cvFile, setCvFile] = useState(null);
  const [pbAbout, setPbAbout] = useState('');
  const [pbExtract, setPbExtract] = useState(null);        // aus Lebenslauf/Freitext ausgelesene Angaben
  const [pbPosts, setPbPosts] = useState([]);              // eigene Posts aus dem LinkedIn-Export
  const [cvSource, setCvSource] = useState(null);         // Eintrag „diese Datei wurde gelesen“ (Name, Zahlen, Kernpunkte)
  const [extracting, setExtracting] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // Arbeitsumfeld: pro Beschäftigung beliebig viele Quellen (Websites, Projekte, Dokumente) plus eigene Worte
  const [cardData, setCardData] = useState({});           // Index der Karte → { websites, siteInput, projectIds, files, docIds, about }
  const [importable, setImportable] = useState([]);       // Dokumente aus dem Wissen anderer Projekte
  const [createdPerson, setCreatedPerson] = useState(null);
  const T = (de, en) => (lang === 'de' ? de : en);
  const [relationship, setRelationship] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [nameError, setNameError] = useState('');
  const [sourceProjectId, setSourceProjectId] = useState('');
  const [projects, setProjects] = useState([]);
  const [cloneElements, setCloneElements] = useState([]);
  const [creating, setCreating] = useState(false);

  // Source project element data (fetched for preview)
  const [sourceData, setSourceData] = useState(null);

  // ── Gleiche Marke data ──
  const [personName, setPersonName] = useState('');
  const [personRole, setPersonRole] = useState('');
  const [personBio, setPersonBio] = useState('');
  const [voiceDesc, setVoiceDesc] = useState('');
  const [activePlatforms, setActivePlatforms] = useState([]);
  const [inspirations, setInspirations] = useState(['', '']);

  // ── Ähnliche Marke extended data ──
  const [hasOwnWebsite, setHasOwnWebsite] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  // Media
  const [selectedMediaIds, setSelectedMediaIds] = useState([]);
  // Writing style
  const [writingTone, setWritingTone] = useState('');
  const [writingAddressing, setWritingAddressing] = useState('Du');
  const [writingExamples, setWritingExamples] = useState(['', '']);
  // Channel settings
  const [channelSettings, setChannelSettings] = useState([]);
  // GEO
  const [geoSettings, setGeoSettings] = useState({});
  // SEO
  const [seoSettings, setSeoSettings] = useState({});
  // Inspirations
  const [selectedInspIds, setSelectedInspIds] = useState([]);
  const [newInspirations, setNewInspirations] = useState(['']);

  useEffect(() => () => { mounted.current = false; }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects`, { headers: getAuthHeader() });
      const all = data.projects || [];
      setProjects(all);
      const def = all.find(p => p.is_default);
      if (def) setSourceProjectId(def.id);
      else if (all.length) setSourceProjectId(all[0].id);
    } catch {}
  }, [getAuthHeader]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // Fetch source data when source is selected and relationship is similar_brand
  const fetchSourceData = useCallback(async (srcId) => {
    if (!srcId) return;
    try {
      const { data } = await axios.get(`${API}/projects/${srcId}/element-data`, { headers: getAuthHeader() });
      setSourceData(data);
      // Pre-fill channel settings from source
      if (data.channel_settings?.length) {
        setChannelSettings(data.channel_settings.map(c => ({
          platform: c.platform,
          frequency_per_week: c.frequency_per_week || 1,
          active: c.active !== false,
          custom_limits: c.custom_limits || {},
        })));
      }
      // Pre-fill GEO/SEO from source
      if (data.geo_settings) {
        const { id, user_id, project_id, created_at, updated_at, ...geo } = data.geo_settings;
        setGeoSettings(geo);
      }
      if (data.seo_settings) {
        const { id, user_id, project_id, created_at, updated_at, ...seo } = data.seo_settings;
        setSeoSettings(seo);
      }
      // Pre-fill writing style
      if (data.writing_style?.writingStyle?.tone) {
        setWritingTone(data.writing_style.writingStyle.tone);
      }
      if (data.writing_style?.writingStyle?.addressing) {
        setWritingAddressing(data.writing_style.writingStyle.addressing);
      }
    } catch {}
  }, [getAuthHeader]);

  const toggleElement = (id) => setCloneElements(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);
  const togglePlatform = (p) => setActivePlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  const handleCancel = () => { onCancel(); };

  const handleRelationshipSelect = (id) => {
    setRelationship(id);
    if (id === 'same_brand') setCloneElements(['brand_media', 'channel_settings', 'brand_geo_settings', 'brand_seo_settings']);
    else setCloneElements([]);
    setStep('name');
  };

  const validateName = () => {
    const name = projectName.trim();
    if (!name) return false;
    if (projects.some(p => p.name.toLowerCase() === name.toLowerCase())) {
      setNameError('Ein Projekt mit diesem Namen existiert bereits');
      return false;
    }
    setNameError('');
    return true;
  };

  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (!validateName()) return;
    if (projectType === 'person') { setPbBasics(b => ({ ...b, name: b.name || projectName.trim() })); setStep('person_basics'); return; }
    if (relationship === 'independent') { createIndependentProject(); return; }
    if (projects.length <= 1) {
      if (projects.length === 1) setSourceProjectId(projects[0].id);
      if (relationship === 'same_brand') setStep('setup_person');
      else { fetchSourceData(projects[0]?.id); setStep('elements'); }
    } else {
      setStep('source');
    }
  };

  const handleSourceNext = () => {
    if (!sourceProjectId) return;
    if (relationship === 'same_brand') setStep('setup_person');
    else { fetchSourceData(sourceProjectId); setStep('elements'); }
  };

  const handleElementsSubmit = () => {
    if (cloneElements.length === 0) { createIndependentProject(); return; }
    setStep('voice_diff');
  };

  // ── "Ähnliche Marke" unchecked step sequence ──
  const getUncheckedSteps = useCallback(() => {
    return CLONE_ELEMENTS.filter(el => !cloneElements.includes(el.id)).map(el => el.setupStep);
  }, [cloneElements]);

  const getAllSimilarSteps = useCallback(() => {
    return ['voice_diff', 'website_question', ...getUncheckedSteps(), 'similar_finish'];
  }, [getUncheckedSteps]);

  const getNextStep = useCallback((current) => {
    const steps = getAllSimilarSteps();
    const idx = steps.indexOf(current);
    return idx >= 0 && idx < steps.length - 1 ? steps[idx + 1] : 'similar_finish';
  }, [getAllSimilarSteps]);

  const getPrevStep = useCallback((current) => {
    const steps = getAllSimilarSteps();
    const idx = steps.indexOf(current);
    return idx > 0 ? steps[idx - 1] : 'elements';
  }, [getAllSimilarSteps]);

  const getStepNum = useCallback((current) => {
    const steps = getAllSimilarSteps();
    return steps.indexOf(current) + 1;
  }, [getAllSimilarSteps]);

  const totalSimilarSteps = getAllSimilarSteps().length;

  // ── CREATE: Independent project ──
  const createIndependentProject = async () => {
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/projects`, { name: projectName.trim() }, { headers: getAuthHeader() });
      await setActiveProjectId(data.project.id);
      onComplete(data.project, 'onboarding');
    } catch (err) {
      if (err.response?.status === 409) { setNameError(err.response.data.detail); setStep('name'); }
      else toast.error(err.response?.data?.detail || (lang === 'de' ? 'Fehler beim Erstellen' : 'Error creating project'));
    } finally { if (mounted.current) setCreating(false); }
  };

  // ── FINAL: Clone + setup ──
  const createClonedProject = async () => {
    setCreating(true);
    try {
      const elements = relationship === 'same_brand'
        ? ['presence_profile', 'brand_media', 'channel_settings', 'brand_geo_settings', 'brand_seo_settings']
        : cloneElements;
      const srcId = projects.length === 1 ? projects[0].id : sourceProjectId;

      const { data } = await axios.post(`${API}/projects/clone`, {
        name: projectName.trim(), source_project_id: srcId, clone_elements: elements,
      }, { headers: getAuthHeader() });

      const newProject = data.project;
      const setupData = {};

      if (relationship === 'same_brand') {
        // Gleiche Marke: person, voice, platforms, inspirations
        if (personName || personRole || personBio) {
          setupData.brand_person_name = personName;
          setupData.brand_person_role = personRole;
          setupData.brand_person_bio = personBio;
        }
        if (voiceDesc) setupData.voice_description = voiceDesc;
        if (activePlatforms.length) setupData.active_platforms = activePlatforms;
        const validInsps = inspirations.filter(t => t.trim());
        if (validInsps.length) setupData.inspirations = validInsps;
      } else {
        // Ähnliche Marke: voice_diff + all unchecked element data
        if (voiceDesc) setupData.voice_description = voiceDesc;
        if (websiteUrl) setupData.website_url = websiteUrl;
        // Unchecked elements → send their data
        if (!cloneElements.includes('channel_settings') && channelSettings.length) {
          setupData.channel_settings = channelSettings;
        }
        if (!cloneElements.includes('brand_geo_settings') && Object.keys(geoSettings).length) {
          setupData.geo_settings = geoSettings;
        }
        if (!cloneElements.includes('brand_seo_settings') && Object.keys(seoSettings).length) {
          setupData.seo_settings = seoSettings;
        }
        if (!cloneElements.includes('writing_style')) {
          setupData.writing_style = {
            tonality: writingTone,
            writingStyle: { addressing: writingAddressing, tone: writingTone },
            fewShotExamples: writingExamples.filter(t => t.trim()),
          };
        }
        if (!cloneElements.includes('brand_media') && selectedMediaIds.length) {
          setupData.copy_media_ids = selectedMediaIds;
        }
        if (!cloneElements.includes('brand_inspirations')) {
          if (selectedInspIds.length) setupData.copy_inspiration_ids = selectedInspIds;
          const validNew = newInspirations.filter(t => t.trim());
          if (validNew.length) setupData.inspirations = validNew;
        }
      }

      if (Object.keys(setupData).length) {
        await axios.post(`${API}/projects/${newProject.id}/setup`, setupData, { headers: getAuthHeader() });
      }

      await setActiveProjectId(newProject.id);
      toast.success(`Projekt "${newProject.name}" ist bereit`);
      onComplete(newProject);
    } catch (err) {
      if (err.response?.status === 409) { setNameError(err.response.data.detail); setStep('name'); }
      else toast.error(err.response?.data?.detail || (lang === 'de' ? 'Fehler beim Erstellen' : 'Error creating project'));
    } finally { if (mounted.current) setCreating(false); }
  };

  // ── Persönliche Marke: Projekt anlegen. Danach läuft je nach Arbeitsumfeld entweder direkt der stille
  //    Profil-Schritt (Import, Dokumente, überspringen) oder die normale Website-Analyse.
  const companyProjects = projects.filter(p => p.project_type !== 'person');
  // Eine Karte je Beschäftigung. Erkennt der Lebenslauf mehrere laufende Rollen, bekommt jede ihre eigene Karte.
  const cardRoles = (pbExtract?.roles?.length >= 2)
    ? pbExtract.roles.map(r => ({ id: r.id || '', job: r.job || '', company: r.company || '', description: r.description || '' }))
    : [{ id: '', job: pbExtract?.job || '', company: pbExtract?.company || T('Mein Unternehmen', 'My company'), description: '' }];
  const multiRole = cardRoles.length >= 2;
  const card = (i) => ({ websites: [], siteInput: '', projectIds: [], files: [], docIds: [], about: '', ...(cardData[i] || {}) });
  const updCard = (i, patch) => setCardData(d => ({ ...d, [i]: { ...({ websites: [], siteInput: '', projectIds: [], files: [], docIds: [], about: '' }), ...(d[i] || {}), ...patch } }));
  const addSite = (i) => {
    const v = card(i).siteInput.trim();
    if (!v) return;
    updCard(i, { websites: [...card(i).websites.filter(w => w !== v), v], siteInput: '' });
  };
  const MAX_CV_BYTES = 10 * 1024 * 1024;
  const DOC_EXT = ['pdf', 'docx', 'txt', 'md'];
  const extOf = (f) => (f.name.split('.').pop() || '').toLowerCase();

  const pickCv = (file) => {
    if (!file) return;
    const ext = extOf(file);
    if (!['pdf', 'docx', 'zip'].includes(ext)) { setPbError(T('Bitte PDF, DOCX oder den LinkedIn-Export (ZIP) hochladen.', 'Please upload a PDF, DOCX or the LinkedIn export (ZIP).')); return; }
    if (file.size > (ext === 'zip' ? 80 * 1024 * 1024 : MAX_CV_BYTES)) { setPbError(ext === 'zip' ? T('Die Datei ist größer als 80 MB.', 'The file is larger than 80 MB.') : T('Die Datei ist größer als 10 MB.', 'The file is larger than 10 MB.')); return; }
    setPbError(''); setCvFile(file); setPbExtract(null); setPbPosts([]); setCvSource(null);
  };

  const runExtract = async () => {
    setExtracting(true); setPbError('');
    try {
      const form = new FormData();
      if (cvFile) form.append('file', cvFile);
      form.append('about', pbAbout.trim());
      form.append('name', pbBasics.name.trim());
      form.append('language', lang);
      const { data } = await axios.post(`${API}/projects/personal-brand/extract`, form, { headers: getAuthHeader(), timeout: 120000 });
      setPbExtract(data.profile);
      setPbPosts(data.posts || []);
      setCvSource(data.source || null);
    } catch (err) {
      setPbError(err.response?.data?.detail || T('Die Angaben konnten nicht ausgelesen werden.', 'Could not read the details.'));
    } finally { if (mounted.current) setExtracting(false); }
  };

  const openWorkStep = async () => {
    setStep('person_work');
    try {
      const { data } = await axios.get(`${API}/knowledge/importable`, { headers: getAuthHeader() });
      setImportable(data.documents || []);
    } catch { /* ohne Wissen-Auswahl weiter */ }
  };

  const addCardFiles = (i, list) => {
    const ok = Array.from(list || []).filter(f => DOC_EXT.includes(extOf(f)) && f.size <= MAX_CV_BYTES);
    if (ok.length < (list || []).length) toast.error(T('Nur PDF, DOCX, TXT oder MD bis 10 MB.', 'Only PDF, DOCX, TXT or MD up to 10 MB.'));
    updCard(i, { files: [...card(i).files, ...ok].slice(0, 10) });
  };

  const buildPersonalBasics = (withNotes = true) => ({
    ...(pbExtract || {}),
    name: pbBasics.name.trim(),
    gender: pbBasics.gender === 'none' ? '' : (pbBasics.gender || ''),
    about: pbAbout.trim(),
    work_about: !multiRole && withNotes ? card(0).about.trim() : '',
    cv_sources: cvSource ? [cvSource] : [],
    roles: cardRoles.map((r, i) => ({ ...r, description: (withNotes ? card(i).about.trim() : '') || r.description })),
  });

  // Eigene Posts aus dem LinkedIn-Export als Stil-Vorbilder übernehmen (Fehler dürfen das Anlegen nie verhindern)
  const importPosts = async (projectId) => {
    if (!pbPosts.length) return;
    try {
      await axios.post(`${API}/projects/${projectId}/voice/samples/import`, { posts: pbPosts, platform: 'linkedin' }, { headers: getAuthHeader() });
    } catch { /* Posts lassen sich später in der Brand DNA ergänzen */ }
  };

  const createPersonProject = async (skip = false) => {
    const name = pbBasics.name.trim();
    if (projects.some(p => p.name.toLowerCase() === name.toLowerCase())) {
      setNameError(T('Ein Projekt mit diesem Namen existiert bereits', 'A project with this name already exists'));
      setStep('person_basics');
      return;
    }
    setProjectName(name);
    setCreating(true);
    setPbError('');
    try {
      const personal_basics = buildPersonalBasics(!skip);
      // Nur die Optik (Farben, Logo, Bilder) kommt vom ersten gewählten Projekt; die Inhalte aller Quellen gehen als Kontext an die jeweilige Beschäftigung
      const cloneFrom = skip ? '' : cardRoles.flatMap((_, i) => card(i).projectIds)[0];
      const { data } = cloneFrom
        ? await axios.post(`${API}/projects/clone`, { name, source_project_id: cloneFrom, clone_elements: ['presence_profile'], project_type: 'person', personal_basics }, { headers: getAuthHeader() })
        : await axios.post(`${API}/projects`, { name, project_type: 'person', personal_basics }, { headers: getAuthHeader() });
      const pid = data.project.id;
      await setActiveProjectId(pid);
      await importPosts(pid);

      if (!skip) {
        // Alle Quellen den richtigen Beschäftigungen zuordnen. Einzelne Fehler stoppen das Anlegen nicht.
        const roleIds = (data.project.personal_basics?.roles || []).map(r => r.id);
        const fail = (what) => (err) => toast.error(err.response?.data?.detail || `${what}: ${T('Das hat nicht geklappt. Du kannst es später ergänzen.', 'That did not work. You can add it later.')}`);
        const jobs = [];
        cardRoles.forEach((_, i) => {
          const rid = roleIds[i];
          const c = card(i);
          if (!rid) return;
          const sites = [...c.websites, ...(c.siteInput.trim() ? [c.siteInput.trim()] : [])];
          sites.forEach(u => jobs.push(axios.post(`${API}/projects/${pid}/roles/${rid}/website`, { url: u, language: lang }, { headers: getAuthHeader(), timeout: 150000 }).catch(fail(u))));
          c.projectIds.forEach(sp => jobs.push(axios.post(`${API}/projects/${pid}/roles/${rid}/project`, { source_project_id: sp }, { headers: getAuthHeader() }).catch(fail(T('Projekt', 'Project')))));
          if (c.docIds.length) jobs.push(axios.post(`${API}/knowledge/import`, { doc_ids: c.docIds, role_id: rid }, { headers: getAuthHeader() }).catch(() => {}));
          c.files.forEach(f => {
            const form = new FormData();
            form.append('file', f); form.append('role_id', rid);
            jobs.push(axios.post(`${API}/knowledge/upload`, form, { headers: getAuthHeader(), timeout: 120000 }).catch(fail(f.name)));
          });
        });
        await Promise.all(jobs);
      }
      setCreatedPerson(data.project);
      setStep('person_finalize');
    } catch (err) {
      if (err.response?.status === 409) { setNameError(err.response.data.detail); setStep('person_basics'); }
      else setPbError(err.response?.data?.detail || T('Fehler beim Erstellen', 'Error creating project'));
    } finally { if (mounted.current) setCreating(false); }
  };

  // Helpers
  const addInspiration = () => setInspirations(prev => [...prev, '']);
  const rmInspiration = (i) => setInspirations(prev => prev.filter((_, idx) => idx !== i));
  const updInspiration = (i, val) => setInspirations(prev => prev.map((v, idx) => idx === i ? val : v));
  const addNewInsp = () => setNewInspirations(prev => [...prev, '']);
  const rmNewInsp = (i) => setNewInspirations(prev => prev.filter((_, idx) => idx !== i));
  const updNewInsp = (i, val) => setNewInspirations(prev => prev.map((v, idx) => idx === i ? val : v));
  const toggleMediaId = (id) => setSelectedMediaIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleInspId = (id) => setSelectedInspIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const updateChannel = (idx, field, val) => setChannelSettings(prev => prev.map((c, i) => i === idx ? { ...c, [field]: val } : c));

  return (
    <div className="fixed inset-0 z-50 bg-[#0A1628]/95 flex items-center justify-center p-4 overflow-y-auto" data-testid="new-project-onboarding">
      <div className={`w-full relative my-8 ${step === 'person_work' ? 'max-w-xl' : 'max-w-lg'}`}>
        <button onClick={handleCancel} className="absolute -top-10 right-0 text-[#5A6C7D] hover:text-white transition-colors" data-testid="onboarding-close">
          <X className="w-5 h-5" />
        </button>

        <AnimatePresence mode="wait">
          {/* ═══ Projekttyp ═══ */}
          {step === 'type' && (
            <motion.div key="type" {...anim}>
              <h2 className="text-xl font-semibold text-white mb-1">{T('Neues Projekt erstellen', 'Create new project')}</h2>
              <p className="text-sm text-[#A0AEC0] mb-6">{T('Für wen erstellst du Content?', 'Who are you creating content for?')}</p>
              <div className="space-y-4">
                {[
                  { id: 'company', icon: Building2, title: T('Unternehmen / Marke', 'Company / brand'), desc: T('Inhalte aus Brand DNA, Produkten und Zielgruppen deiner Marke', 'Content from your brand DNA, products and audiences') },
                  { id: 'person', icon: User, title: T('Person (Personal Brand)', 'Person (personal brand)'), desc: T('Du als Expertin/Experte — Content rund um dich, deinen Werdegang und deine Haltung', 'You as an expert — content built around you, your background and your point of view') },
                ].map(opt => {
                  const Icon = opt.icon;
                  return (
                    <Card key={opt.id} className="bg-[#1A2B42] border-[#1A2B42] hover:border-[#00D9C8]/50 transition-colors cursor-pointer" onClick={() => { setProjectType(opt.id); setStep(opt.id === 'person' ? 'person_basics' : 'relationship'); }} data-testid={`project-type-${opt.id}`}>
                      <CardContent className="p-4 flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-[#00D9C8]/10 flex items-center justify-center shrink-0 mt-0.5"><Icon className="w-5 h-5 text-[#00D9C8]" /></div>
                        <div>
                          <p className="text-white font-medium text-sm">{opt.title}</p>
                          <p className="text-[#A0AEC0] text-xs mt-1">{opt.desc}</p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* ═══ Relationship ═══ */}
          {step === 'relationship' && (
            <motion.div key="rel" {...anim}>
              <BackBtn lang={lang} onClick={() => setStep('type')} />
              <h2 className="text-xl font-semibold text-white mb-1">{lang === 'de' ? 'Neues Projekt erstellen' : 'Create new project'}</h2>
              <p className="text-sm text-[#A0AEC0] mb-6">{lang === 'de' ? 'Wie verhält sich dieses neue Projekt zu deinen bestehenden?' : 'How does this new project relate to your existing ones?'}</p>
              <div className="space-y-4">
                {RELATIONSHIPS.map(opt => {
                  const Icon = opt.icon;
                  return (
                    <Card key={opt.id} className="bg-[#1A2B42] border-[#1A2B42] hover:border-[#00D9C8]/50 transition-colors cursor-pointer" onClick={() => handleRelationshipSelect(opt.id)} data-testid={`relationship-${opt.id}`}>
                      <CardContent className="p-4 flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-[#00D9C8]/10 flex items-center justify-center shrink-0 mt-0.5"><Icon className="w-5 h-5 text-[#00D9C8]" /></div>
                        <div>
                          <p className="text-white font-medium text-sm">{opt.title}</p>
                          <p className="text-[#5A6C7D] text-xs mt-0.5">{opt.desc}</p>
                          <p className="text-[#A0AEC0] text-xs mt-1">{opt.detail}</p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* ═══ Name ═══ */}
          {step === 'name' && (
            <motion.div key="name" {...anim}>
              <BackBtn lang={lang} onClick={() => { setStep(projectType === 'person' ? 'type' : 'relationship'); setNameError(''); }} />
              <h2 className="text-xl font-semibold text-white mb-1">{lang === 'de' ? 'Projektname' : 'Project name'}</h2>
              <p className="text-sm text-[#A0AEC0] mb-6">Gib deinem neuen Projekt einen Namen.</p>
              <form onSubmit={handleNameSubmit}>
                <Input autoFocus value={projectName} onChange={e => { setProjectName(e.target.value); setNameError(''); }} placeholder="z.B. Mein Klient AG" className={`bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D] mb-1 ${nameError ? 'border-red-500' : ''}`} data-testid="project-name-input" />
                {nameError && <p className="text-red-400 text-xs mb-3" data-testid="name-error">{nameError}</p>}
                {!nameError && <div className="mb-3" />}
                <SubmitBtn type="submit" loading={creating} disabled={!projectName.trim()}>
                  {projectType === 'person' ? T('Weiter', 'Continue') : relationship === 'independent' ? (lang === 'de' ? 'Erstellen & Onboarding starten' : 'Create & Start Onboarding') : (lang === 'de' ? 'Weiter' : 'Continue')}
                </SubmitBtn>
              </form>
            </motion.div>
          )}

          {/* ═══ Source ═══ */}
          {step === 'source' && (
            <motion.div key="source" {...anim}>
              <BackBtn lang={lang} onClick={() => setStep('name')} />
              <h2 className="text-xl font-semibold text-white mb-1">Quell-Projekt wählen</h2>
              <p className="text-sm text-[#A0AEC0] mb-6">Von welchem Projekt sollen Daten kopiert werden?</p>
              <div className="space-y-2 mb-4">
                {projects.map(p => (
                  <Card key={p.id} className={`cursor-pointer transition-colors ${sourceProjectId === p.id ? 'bg-[#00D9C8]/10 border-[#00D9C8]' : 'bg-[#1A2B42] border-[#1A2B42] hover:border-[#00D9C8]/30'}`} onClick={() => setSourceProjectId(p.id)} data-testid={`source-project-${p.id}`}>
                    <CardContent className="p-3 flex items-center justify-between">
                      <span className="text-white text-sm">{p.name}</span>
                      {sourceProjectId === p.id && <Check className="w-4 h-4 text-[#00D9C8]" />}
                    </CardContent>
                  </Card>
                ))}
              </div>
              <SubmitBtn disabled={!sourceProjectId} onClick={handleSourceNext}>{lang === 'de' ? 'Weiter' : 'Continue'}</SubmitBtn>
            </motion.div>
          )}

          {/* ═══ Elements (Ähnliche Marke) ═══ */}
          {step === 'elements' && (
            <motion.div key="elements" {...anim}>
              <BackBtn lang={lang} onClick={() => projects.length > 1 ? setStep('source') : setStep('name')} />
              <h2 className="text-xl font-semibold text-white mb-1">Was soll kopiert werden?</h2>
              <p className="text-sm text-[#A0AEC0] mb-6">Markierte Elemente werden übernommen. Nicht markierte musst du neu konfigurieren.</p>
              <div className="space-y-2 mb-6">
                {CLONE_ELEMENTS.map(el => {
                  const ElIcon = el.icon;
                  return (
                    <label key={el.id} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${cloneElements.includes(el.id) ? 'bg-[#00D9C8]/10 border border-[#00D9C8]/40' : 'bg-[#1A2B42] border border-[#1A2B42] hover:border-[#00D9C8]/20'}`} data-testid={`clone-element-${el.id}`}>
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${cloneElements.includes(el.id) ? 'bg-[#00D9C8] border-[#00D9C8]' : 'border-[#5A6C7D]'}`}>
                        {cloneElements.includes(el.id) && <Check className="w-3 h-3 text-[#0A1628]" />}
                      </div>
                      <ElIcon className="w-4 h-4 text-[#A0AEC0]" />
                      <span className="text-white text-sm">{el.label}</span>
                      <input type="checkbox" className="sr-only" checked={cloneElements.includes(el.id)} onChange={() => toggleElement(el.id)} />
                    </label>
                  );
                })}
              </div>
              <SubmitBtn loading={creating} onClick={handleElementsSubmit}>
                {cloneElements.length === 0 ? 'Erstellen & Onboarding starten' : 'Weiter'}
              </SubmitBtn>
            </motion.div>
          )}

          {/* ═══ Voice Diff (Ähnliche Marke mandatory) ═══ */}
          {step === 'voice_diff' && (
            <motion.div key="vd" {...anim}>
              <BackBtn lang={lang} onClick={() => setStep('elements')} />
              <StepBadge n={getStepNum('voice_diff')} total={totalSimilarSteps} />
              <h2 className="text-xl font-semibold text-white mb-1">Was unterscheidet dieses Projekt?</h2>
              <p className="text-sm text-[#A0AEC0] mb-5">Beschreibe, was den Voice und die Ausrichtung vom Quell-Projekt unterscheidet.</p>
              <Textarea value={voiceDesc} onChange={e => setVoiceDesc(e.target.value)} placeholder="z.B. Fokus auf B2B, formellerer Ton, technischere Sprache..." rows={4} className="bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D] resize-none mb-5" data-testid="voice-diff-input" />
              <SubmitBtn onClick={() => setStep(getNextStep('voice_diff'))}>Weiter</SubmitBtn>
            </motion.div>
          )}

          {/* ═══ Website Question ═══ */}
          {step === 'website_question' && (
            <motion.div key="wq" {...anim}>
              <BackBtn lang={lang} onClick={() => setStep(getPrevStep('website_question'))} />
              <StepBadge n={getStepNum('website_question')} total={totalSimilarSteps} />
              <h2 className="text-xl font-semibold text-white mb-1">Hat diese Marke eine eigene Website?</h2>
              <p className="text-sm text-[#A0AEC0] mb-6">Falls ja, können wir die Website analysieren.</p>
              <div className="space-y-3 mb-5">
                <Card className={`cursor-pointer transition-colors ${hasOwnWebsite === true ? 'bg-[#00D9C8]/10 border-[#00D9C8]' : 'bg-[#1A2B42] border-[#1A2B42] hover:border-[#00D9C8]/30'}`} onClick={() => setHasOwnWebsite(true)} data-testid="website-yes">
                  <CardContent className="p-4 flex items-center gap-3">
                    <Globe className="w-5 h-5 text-[#00D9C8]" /><div><p className="text-white text-sm font-medium">Ja, Website analysieren</p></div>
                  </CardContent>
                </Card>
                <Card className={`cursor-pointer transition-colors ${hasOwnWebsite === false ? 'bg-[#00D9C8]/10 border-[#00D9C8]' : 'bg-[#1A2B42] border-[#1A2B42] hover:border-[#00D9C8]/30'}`} onClick={() => { setHasOwnWebsite(false); setWebsiteUrl(''); }} data-testid="website-no">
                  <CardContent className="p-4 flex items-center gap-3">
                    <Pencil className="w-5 h-5 text-[#A0AEC0]" /><div><p className="text-white text-sm font-medium">Nein, manuell eingeben</p></div>
                  </CardContent>
                </Card>
              </div>
              {hasOwnWebsite && <Input value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://www.beispiel.de" className="bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D] mb-5" data-testid="website-url-input" />}
              <SubmitBtn disabled={hasOwnWebsite === null || (hasOwnWebsite && !websiteUrl.trim())} onClick={() => setStep(getNextStep('website_question'))}>{lang === 'de' ? 'Weiter' : 'Continue'}</SubmitBtn>
            </motion.div>
          )}

          {/* ═══ Setup: Bildsprache / Brand Media ═══ */}
          {step === 'setup_media' && (
            <motion.div key="sm" {...anim}>
              <BackBtn lang={lang} onClick={() => setStep(getPrevStep('setup_media'))} />
              <StepBadge n={getStepNum('setup_media')} total={totalSimilarSteps} />
              <h2 className="text-xl font-semibold text-white mb-1">Bildsprache & Logo</h2>
              <p className="text-sm text-[#A0AEC0] mb-5">Wähle Medien vom Quell-Projekt zum Kopieren oder lade später eigene hoch.</p>
              {sourceData?.brand_media?.length > 0 ? (
                <div className="grid grid-cols-3 gap-2 mb-5">
                  {sourceData.brand_media.map(m => (
                    <div key={m.id} onClick={() => toggleMediaId(m.id)} className={`relative rounded-lg overflow-hidden cursor-pointer border-2 transition-colors ${selectedMediaIds.includes(m.id) ? 'border-[#00D9C8]' : 'border-transparent hover:border-[#00D9C8]/30'}`} data-testid={`media-${m.id}`}>
                      <img src={m.file_url} alt={m.filename} className="w-full h-20 object-cover" />
                      {selectedMediaIds.includes(m.id) && <div className="absolute top-1 right-1 w-5 h-5 bg-[#00D9C8] rounded-full flex items-center justify-center"><Check className="w-3 h-3 text-[#0A1628]" /></div>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-[#1A2B42] rounded-lg p-4 mb-5 text-center">
                  <Image className="w-8 h-8 text-[#5A6C7D] mx-auto mb-2" />
                  <p className="text-[#5A6C7D] text-sm">Keine Medien im Quell-Projekt vorhanden.</p>
                  <p className="text-[#A0AEC0] text-xs mt-1">Du kannst später in den Einstellungen Medien hochladen.</p>
                </div>
              )}
              <SubmitBtn onClick={() => setStep(getNextStep('setup_media'))}>
                {selectedMediaIds.length > 0 ? `${selectedMediaIds.length} kopieren & weiter` : 'Überspringen'}
              </SubmitBtn>
            </motion.div>
          )}

          {/* ═══ Setup: Schreibstil ═══ */}
          {step === 'setup_writing' && (
            <motion.div key="sw" {...anim}>
              <BackBtn lang={lang} onClick={() => setStep(getPrevStep('setup_writing'))} />
              <StepBadge n={getStepNum('setup_writing')} total={totalSimilarSteps} />
              <h2 className="text-xl font-semibold text-white mb-1">Schreibstil & Tonalität</h2>
              <p className="text-sm text-[#A0AEC0] mb-5">Definiere den Schreibstil für dieses Projekt.</p>
              <div className="space-y-4 mb-5">
                <div>
                  <label className="text-xs text-[#A0AEC0] mb-1 block">Tonalität</label>
                  <Input value={writingTone} onChange={e => setWritingTone(e.target.value)} placeholder="z.B. professionell, nahbar, inspirierend" className="bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D]" data-testid="writing-tone" />
                </div>
                <div>
                  <label className="text-xs text-[#A0AEC0] mb-2 block">Ansprache</label>
                  <div className="flex gap-2">
                    {['Du', 'Sie'].map(opt => (
                      <button key={opt} onClick={() => setWritingAddressing(opt)} className={`px-4 py-1.5 rounded-lg text-sm transition-colors ${writingAddressing === opt ? 'bg-[#00D9C8] text-[#0A1628]' : 'bg-[#1A2B42] text-[#A0AEC0]'}`} data-testid={`addressing-${opt.toLowerCase()}`}>{opt}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#A0AEC0] mb-1 block">Textbeispiele (optional)</label>
                  {writingExamples.map((text, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <Textarea value={text} onChange={e => setWritingExamples(prev => prev.map((v, idx) => idx === i ? e.target.value : v))} placeholder={`Beispieltext ${i + 1}...`} rows={2} className="bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D] resize-none flex-1" data-testid={`writing-example-${i}`} />
                      {writingExamples.length > 1 && <button onClick={() => setWritingExamples(prev => prev.filter((_, idx) => idx !== i))} className="text-[#5A6C7D] hover:text-red-400 self-start mt-2"><Minus className="w-4 h-4" /></button>}
                    </div>
                  ))}
                  {writingExamples.length < 4 && <button onClick={() => setWritingExamples(prev => [...prev, ''])} className="text-[#00D9C8] text-xs flex items-center gap-1 hover:underline"><Plus className="w-3 h-3" /> Beispiel</button>}
                </div>
              </div>
              <SubmitBtn onClick={() => setStep(getNextStep('setup_writing'))}>{lang === 'de' ? 'Weiter' : 'Continue'}</SubmitBtn>
            </motion.div>
          )}

          {/* ═══ Setup: Plattformregeln & Limits ═══ */}
          {step === 'setup_channels' && (
            <motion.div key="sc" {...anim}>
              <BackBtn lang={lang} onClick={() => setStep(getPrevStep('setup_channels'))} />
              <StepBadge n={getStepNum('setup_channels')} total={totalSimilarSteps} />
              <h2 className="text-xl font-semibold text-white mb-1">Plattformregeln & Limits</h2>
              <p className="text-sm text-[#A0AEC0] mb-5">Einstellungen vom Quell-Projekt. Passe an was nötig ist.</p>
              {channelSettings.length > 0 ? (
                <div className="space-y-2 mb-5 max-h-64 overflow-y-auto pr-1">
                  {channelSettings.map((ch, i) => (
                    <div key={i} className="flex items-center gap-3 bg-[#1A2B42] rounded-lg p-3" data-testid={`channel-${ch.platform}`}>
                      <Switch checked={ch.active} onCheckedChange={(v) => updateChannel(i, 'active', v)} data-testid={`channel-switch-${ch.platform}`} />
                      <span className={`text-sm flex-1 ${ch.active ? 'text-white' : 'text-[#5A6C7D]'}`}>{ch.platform}</span>
                      <div className="flex items-center gap-1">
                        <Input type="number" min={0} max={14} value={ch.frequency_per_week} onChange={e => updateChannel(i, 'frequency_per_week', parseInt(e.target.value) || 0)} className="w-14 h-7 bg-[#0A1628] border-[#2A3B52] text-white text-center text-xs" data-testid={`channel-freq-${ch.platform}`} />
                        <span className="text-[#5A6C7D] text-xs">/Wo.</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-[#1A2B42] rounded-lg p-4 mb-5 text-center">
                  <Settings2 className="w-8 h-8 text-[#5A6C7D] mx-auto mb-2" />
                  <p className="text-[#5A6C7D] text-sm">Keine Plattformeinstellungen im Quell-Projekt.</p>
                  <p className="text-[#A0AEC0] text-xs mt-1">Kannst du später in den Einstellungen konfigurieren.</p>
                </div>
              )}
              <SubmitBtn onClick={() => setStep(getNextStep('setup_channels'))}>{lang === 'de' ? 'Weiter' : 'Continue'}</SubmitBtn>
            </motion.div>
          )}

          {/* ═══ Setup: GEO ═══ */}
          {step === 'setup_geo' && (
            <motion.div key="sg" {...anim}>
              <BackBtn lang={lang} onClick={() => setStep(getPrevStep('setup_geo'))} />
              <StepBadge n={getStepNum('setup_geo')} total={totalSimilarSteps} />
              <h2 className="text-xl font-semibold text-white mb-1">GEO-Einstellungen</h2>
              <p className="text-sm text-[#A0AEC0] mb-5">Geographische Ausrichtung für dieses Projekt.</p>
              <div className="space-y-3 mb-5">
                {[
                  { key: 'local_focus', label: 'Lokaler Fokus', desc: 'Inhalte auf eine bestimmte Region ausrichten' },
                  { key: 'include_local_trends', label: 'Lokale Trends einbeziehen', desc: 'Regionale Trends und Events berücksichtigen' },
                  { key: 'multi_region', label: 'Multi-Region', desc: 'Inhalte für mehrere Regionen optimieren' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between bg-[#1A2B42] rounded-lg p-3" data-testid={`geo-${item.key}`}>
                    <div>
                      <p className="text-white text-sm">{item.label}</p>
                      <p className="text-[#5A6C7D] text-xs">{item.desc}</p>
                    </div>
                    <Switch checked={!!geoSettings[item.key]} onCheckedChange={(v) => setGeoSettings(prev => ({ ...prev, [item.key]: v }))} />
                  </div>
                ))}
                <div>
                  <label className="text-xs text-[#A0AEC0] mb-1 block">Zielregion (optional)</label>
                  <Input value={geoSettings.target_region || ''} onChange={e => setGeoSettings(prev => ({ ...prev, target_region: e.target.value }))} placeholder="z.B. DACH, Europa, Global" className="bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D]" data-testid="geo-region" />
                </div>
              </div>
              <SubmitBtn onClick={() => setStep(getNextStep('setup_geo'))}>{lang === 'de' ? 'Weiter' : 'Continue'}</SubmitBtn>
            </motion.div>
          )}

          {/* ═══ Setup: SEO ═══ */}
          {step === 'setup_seo' && (
            <motion.div key="ss" {...anim}>
              <BackBtn lang={lang} onClick={() => setStep(getPrevStep('setup_seo'))} />
              <StepBadge n={getStepNum('setup_seo')} total={totalSimilarSteps} />
              <h2 className="text-xl font-semibold text-white mb-1">SEO-Einstellungen</h2>
              <p className="text-sm text-[#A0AEC0] mb-5">Suchmaschinenoptimierung für dieses Projekt.</p>
              <div className="space-y-3 mb-5">
                {[
                  { key: 'optimize_titles', label: 'Titel optimieren', desc: 'SEO-optimierte Überschriften generieren' },
                  { key: 'include_keywords', label: 'Keywords einbinden', desc: 'Relevante Keywords in Inhalte integrieren' },
                  { key: 'meta_descriptions', label: lang === 'de' ? 'Meta-Beschreibungen' : 'Meta Descriptions', desc: lang === 'de' ? 'Meta-Beschreibungen automatisch erstellen' : 'Auto-generate meta descriptions' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between bg-[#1A2B42] rounded-lg p-3" data-testid={`seo-${item.key}`}>
                    <div>
                      <p className="text-white text-sm">{item.label}</p>
                      <p className="text-[#5A6C7D] text-xs">{item.desc}</p>
                    </div>
                    <Switch checked={!!seoSettings[item.key]} onCheckedChange={(v) => setSeoSettings(prev => ({ ...prev, [item.key]: v }))} />
                  </div>
                ))}
                <div>
                  <label className="text-xs text-[#A0AEC0] mb-1 block">Fokus-Keywords (optional)</label>
                  <Input value={seoSettings.focus_keywords || ''} onChange={e => setSeoSettings(prev => ({ ...prev, focus_keywords: e.target.value }))} placeholder="z.B. SaaS, B2B Marketing, Automation" className="bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D]" data-testid="seo-keywords" />
                </div>
              </div>
              <SubmitBtn onClick={() => setStep(getNextStep('setup_seo'))}>{lang === 'de' ? 'Weiter' : 'Continue'}</SubmitBtn>
            </motion.div>
          )}

          {/* ═══ Setup: Inspirationen ═══ */}
          {step === 'setup_inspirations' && (
            <motion.div key="si" {...anim}>
              <BackBtn lang={lang} onClick={() => {
                if (relationship === 'same_brand') setStep('setup_platforms');
                else setStep(getPrevStep('setup_inspirations'));
              }} />
              {relationship === 'same_brand'
                ? <StepBadge n={4} total={4} />
                : <StepBadge n={getStepNum('setup_inspirations')} total={totalSimilarSteps} />
              }
              <h2 className="text-xl font-semibold text-white mb-1">Inspiration-Beispiele</h2>
              <p className="text-sm text-[#A0AEC0] mb-5">
                {relationship === 'same_brand' ? 'Füge 2-3 Textbeispiele hinzu.' : 'Kopiere Beispiele vom Quell-Projekt oder füge neue hinzu.'}
              </p>

              {/* Same brand: simple text inputs */}
              {relationship === 'same_brand' && (
                <>
                  <div className="space-y-3 mb-4">
                    {inspirations.map((text, i) => (
                      <div key={i} className="flex gap-2">
                        <Textarea value={text} onChange={e => updInspiration(i, e.target.value)} placeholder={`Beispiel ${i + 1}...`} rows={2} className="bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D] resize-none flex-1" data-testid={`inspiration-${i}`} />
                        {inspirations.length > 1 && <button onClick={() => rmInspiration(i)} className="text-[#5A6C7D] hover:text-red-400 self-start mt-2"><Minus className="w-4 h-4" /></button>}
                      </div>
                    ))}
                  </div>
                  {inspirations.length < 5 && <button onClick={addInspiration} className="text-[#00D9C8] text-sm flex items-center gap-1 mb-5 hover:underline" data-testid="add-inspiration"><Plus className="w-4 h-4" /> {lang === 'de' ? 'Weiteres Beispiel' : 'Add example'}</button>}
                  <SubmitBtn loading={creating} onClick={createClonedProject}>{lang === 'de' ? 'Projekt erstellen' : 'Create project'}</SubmitBtn>
                </>
              )}

              {/* Similar brand: copy from source + add new */}
              {relationship !== 'same_brand' && (
                <>
                  {sourceData?.brand_inspirations?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-[#5A6C7D] mb-2">Vom Quell-Projekt kopieren:</p>
                      <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                        {sourceData.brand_inspirations.map(insp => (
                          <label key={insp.id} className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-colors ${selectedInspIds.includes(insp.id) ? 'bg-[#00D9C8]/10 border border-[#00D9C8]/40' : 'bg-[#1A2B42] border border-[#1A2B42]'}`} data-testid={`copy-insp-${insp.id}`}>
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${selectedInspIds.includes(insp.id) ? 'bg-[#00D9C8] border-[#00D9C8]' : 'border-[#5A6C7D]'}`}>
                              {selectedInspIds.includes(insp.id) && <Check className="w-3 h-3 text-[#0A1628]" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              {insp.image_url && <img src={insp.image_url} alt="" className="w-full h-16 object-cover rounded mb-1" />}
                              <p className="text-white text-xs truncate">{insp.text || insp.content || '(Bild)'}</p>
                            </div>
                            <input type="checkbox" className="sr-only" checked={selectedInspIds.includes(insp.id)} onChange={() => toggleInspId(insp.id)} />
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-[#5A6C7D] mb-2">Neue Beispiele:</p>
                  <div className="space-y-2 mb-4">
                    {newInspirations.map((text, i) => (
                      <div key={i} className="flex gap-2">
                        <Textarea value={text} onChange={e => updNewInsp(i, e.target.value)} placeholder={`Neues Beispiel ${i + 1}...`} rows={2} className="bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D] resize-none flex-1" data-testid={`new-insp-${i}`} />
                        {newInspirations.length > 1 && <button onClick={() => rmNewInsp(i)} className="text-[#5A6C7D] hover:text-red-400 self-start mt-2"><Minus className="w-4 h-4" /></button>}
                      </div>
                    ))}
                  </div>
                  {newInspirations.length < 5 && <button onClick={addNewInsp} className="text-[#00D9C8] text-xs flex items-center gap-1 mb-5 hover:underline"><Plus className="w-3 h-3" /> Beispiel</button>}
                  <SubmitBtn onClick={() => setStep(getNextStep('setup_inspirations'))}>{lang === 'de' ? 'Weiter' : 'Continue'}</SubmitBtn>
                </>
              )}
            </motion.div>
          )}

          {/* ═══ Person (Gleiche Marke 1/4) ═══ */}
          {step === 'setup_person' && (
            <motion.div key="person" {...anim}>
              <BackBtn lang={lang} onClick={() => projects.length > 1 ? setStep('source') : setStep('name')} />
              <StepBadge n={1} total={4} />
              <h2 className="text-xl font-semibold text-white mb-1">Person / Brand hinter dem Projekt</h2>
              <p className="text-sm text-[#A0AEC0] mb-5">Wer spricht in diesem Projekt?</p>
              <div className="space-y-3 mb-5">
                <Input value={personName} onChange={e => setPersonName(e.target.value)} placeholder="Name" className="bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D]" data-testid="person-name" />
                <Input value={personRole} onChange={e => setPersonRole(e.target.value)} placeholder="Rolle" className="bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D]" data-testid="person-role" />
                <Textarea value={personBio} onChange={e => setPersonBio(e.target.value)} placeholder="Kurze Bio" rows={3} className="bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D] resize-none" data-testid="person-bio" />
              </div>
              <SubmitBtn onClick={() => setStep('setup_voice')}>{lang === 'de' ? 'Weiter' : 'Continue'}</SubmitBtn>
            </motion.div>
          )}

          {/* ═══ Voice (Gleiche Marke 2/4) ═══ */}
          {step === 'setup_voice' && (
            <motion.div key="voice" {...anim}>
              <BackBtn lang={lang} onClick={() => setStep('setup_person')} />
              <StepBadge n={2} total={4} />
              <h2 className="text-xl font-semibold text-white mb-1">Voice-Unterschied</h2>
              <p className="text-sm text-[#A0AEC0] mb-5">Was unterscheidet die Stimme dieses Projekts?</p>
              <Textarea value={voiceDesc} onChange={e => setVoiceDesc(e.target.value)} placeholder="z.B. Persönlicher, weniger formal..." rows={4} className="bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D] resize-none mb-5" data-testid="voice-desc" />
              <SubmitBtn onClick={() => setStep('setup_platforms')}>{lang === 'de' ? 'Weiter' : 'Continue'}</SubmitBtn>
            </motion.div>
          )}

          {/* ═══ Platforms (Gleiche Marke 3/4) ═══ */}
          {step === 'setup_platforms' && (
            <motion.div key="plat" {...anim}>
              <BackBtn lang={lang} onClick={() => setStep('setup_voice')} />
              <StepBadge n={3} total={4} />
              <h2 className="text-xl font-semibold text-white mb-1">Aktive Plattformen</h2>
              <p className="text-sm text-[#A0AEC0] mb-5">Welche Plattformen sollen aktiv sein?</p>
              <div className="grid grid-cols-2 gap-2 mb-5">
                {PLATFORMS.map(p => (
                  <label key={p} className={`flex items-center gap-2 p-2.5 rounded-lg cursor-pointer text-sm ${activePlatforms.includes(p) ? 'bg-[#00D9C8]/10 border border-[#00D9C8]/40 text-white' : 'bg-[#1A2B42] border border-[#1A2B42] text-[#A0AEC0]'}`} data-testid={`platform-${p.toLowerCase().replace(/[() ]/g, '')}`}>
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${activePlatforms.includes(p) ? 'bg-[#00D9C8] border-[#00D9C8]' : 'border-[#5A6C7D]'}`}>
                      {activePlatforms.includes(p) && <Check className="w-3 h-3 text-[#0A1628]" />}
                    </div>
                    {p}
                    <input type="checkbox" className="sr-only" checked={activePlatforms.includes(p)} onChange={() => togglePlatform(p)} />
                  </label>
                ))}
              </div>
              <SubmitBtn onClick={() => setStep('setup_inspirations')}>{lang === 'de' ? 'Weiter' : 'Continue'}</SubmitBtn>
            </motion.div>
          )}

          {/* ═══ Similar Finish ═══ */}
          {step === 'similar_finish' && (
            <motion.div key="sf" {...anim}>
              <BackBtn lang={lang} onClick={() => setStep(getPrevStep('similar_finish'))} />
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-full bg-[#00D9C8]/10 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-7 h-7 text-[#00D9C8]" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">Alles bereit!</h2>
                <p className="text-sm text-[#A0AEC0] mb-6">
                  Projekt <span className="text-[#00D9C8] font-medium">"{projectName}"</span> wird erstellt.
                </p>
                <SubmitBtn loading={creating} onClick={createClonedProject}>Projekt erstellen</SubmitBtn>
              </div>
            </motion.div>
          )}

          {/* ═══ Person 1/3: Name + Gender ═══ */}
          {step === 'person_basics' && (
            <motion.div key="pbasics" {...anim}>
              <BackBtn lang={lang} onClick={() => { setStep('type'); setNameError(''); }} />
              <PersonStepper current={1} lang={lang} />
              <h2 className="text-xl font-semibold text-white mb-1">{T('Wer bist du?', 'Who are you?')}</h2>
              <p className="text-sm text-[#A0AEC0] mb-5">{T('Dein Name ist auch der Name des Projekts.', 'Your name is also the name of the project.')}</p>
              <form onSubmit={e => { e.preventDefault(); setStep('person_cv'); }} className="space-y-5">
                <div className="space-y-2">
                  <Input autoFocus value={pbBasics.name} onChange={e => { setPbBasics(b => ({ ...b, name: e.target.value })); setProjectName(e.target.value); setNameError(''); }} placeholder={T('Vor- und Nachname', 'First and last name')} className={`bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D] ${nameError ? 'border-red-500' : ''}`} data-testid="pb-name" />
                  {nameError && <p className="text-red-400 text-xs" data-testid="name-error">{nameError}</p>}
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-[#6B7C90] mb-2">{T('Geschlecht', 'Gender')}</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'female', de: 'Weiblich', en: 'Female' },
                      { id: 'male', de: 'Männlich', en: 'Male' },
                      { id: 'diverse', de: 'Divers', en: 'Diverse' },
                      { id: 'none', de: 'Keine Angabe', en: 'Prefer not to say' },
                    ].map(g => (
                      <button type="button" key={g.id} onClick={() => setPbBasics(b => ({ ...b, gender: g.id }))} className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${pbBasics.gender === g.id ? 'border-[#00D9C8] bg-[#00D9C8]/15 text-white' : 'border-[#2A3B52] text-[#A0AEC0] hover:text-white hover:border-[#00D9C8]/40'}`} data-testid={`pb-gender-${g.id}`}>
                        {lang === 'de' ? g.de : g.en}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-[#6B7C90] mt-2">{T('Damit Texte in der richtigen Form für dich geschrieben werden (z. B. „Beraterin“ / „Berater“).', 'So texts use the right forms for you (e.g. job titles in German).')}</p>
                </div>
                <SubmitBtn type="submit" disabled={!pbBasics.name.trim() || !pbBasics.gender}>{T('Weiter', 'Continue')}</SubmitBtn>
              </form>
            </motion.div>
          )}

          {/* ═══ Person 2/3: Lebenslauf ═══ */}
          {step === 'person_cv' && (
            <motion.div key="pcv" {...anim}>
              <BackBtn lang={lang} onClick={() => { setStep('person_basics'); setPbError(''); }} />
              <PersonStepper current={2} lang={lang} />
              <h2 className="text-xl font-semibold text-white mb-1">{T('Dein Lebenslauf', 'Your CV')}</h2>
              <p className="text-sm text-[#A0AEC0] mb-5">{T('Daraus lesen wir Position, Stationen, Ausbildung, Skills und Erfolge heraus. Die Datei selbst speichern wir nicht.', 'We read your position, career, education, skills and achievements from it. We do not store the file itself.')}</p>

              <label className={`flex items-center gap-3 rounded-lg border border-dashed px-4 py-4 cursor-pointer transition-colors ${cvFile ? 'border-[#00D9C8] bg-[#00D9C8]/10' : 'border-[#2A3B52] bg-[#1A2B42] hover:border-[#00D9C8]/50'}`} data-testid="pb-cv-drop">
                {cvFile ? <FileText className="w-5 h-5 text-[#00D9C8] shrink-0" /> : <Upload className="w-5 h-5 text-[#A0AEC0] shrink-0" />}
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-white truncate">{cvFile ? cvFile.name : T('PDF, DOCX oder LinkedIn-Export (ZIP) auswählen', 'Choose a PDF, DOCX or LinkedIn export (ZIP)')}</span>
                  <span className="block text-xs text-[#6B7C90]">{cvFile ? `${(cvFile.size / 1024).toFixed(0)} KB` : T('PDF oder DOCX bis 10 MB, ZIP-Export bis 80 MB', 'PDF or DOCX up to 10 MB, ZIP export up to 80 MB')}</span>
                </span>
                {cvFile && <button type="button" onClick={e => { e.preventDefault(); setCvFile(null); setPbExtract(null); setPbPosts([]); setCvSource(null); }} className="text-[#5A6C7D] hover:text-red-400 p-1" aria-label="Remove" data-testid="pb-cv-remove"><X className="w-4 h-4" /></button>}
                <input type="file" className="sr-only" accept=".pdf,.docx,.zip,application/pdf,application/zip,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={e => { pickCv(e.target.files?.[0]); e.target.value = ''; }} data-testid="pb-cv-input" />
              </label>

              <div className="mt-2 rounded-lg bg-[#1A2B42]" data-testid="pb-linkedin-help">
                <button type="button" onClick={() => setHelpOpen(o => !o)} className="w-full flex items-center gap-2 px-3.5 py-2.5 text-left text-sm text-[#A0AEC0] hover:text-white" aria-expanded={helpOpen}>
                  <ChevronDown className={`w-4 h-4 transition-transform ${helpOpen ? '' : '-rotate-90'}`} />
                  {T('So kannst du dir deinen CV aus LinkedIn holen', 'How to get your CV from LinkedIn')}
                </button>
                {helpOpen && (
                  <ol className="px-3.5 pb-3.5 pl-9 list-decimal space-y-1 text-xs text-[#A0AEC0]">
                    <li>{T('Öffne dein Profil auf linkedin.com.', 'Open your profile on linkedin.com.')}</li>
                    <li>{T('Klicke unter deinem Profilbild auf „Ressourcen“ (in der App: „Mehr“).', 'Click “Resources” below your profile photo (in the app: “More”).')}</li>
                    <li>{T('Wähle „Als PDF speichern“.', 'Choose “Save to PDF”.')}</li>
                    <li>{T('Lade die heruntergeladene PDF-Datei hier hoch.', 'Upload the downloaded PDF here.')}</li>
                  </ol>
                )}
                {helpOpen && (
                  <div className="px-3.5 pb-3.5 text-xs text-[#A0AEC0]" data-testid="pb-linkedin-export-help">
                    <p className="text-white mb-1">{T('Besser: der komplette Export, mit deinen bisherigen Posts', 'Better: the full export, including your past posts')}</p>
                    <ol className="pl-5 list-decimal space-y-1">
                      <li>{T('LinkedIn → „Einstellungen & Datenschutz“ → „Datenschutz“ → „Kopie deiner Daten anfordern“.', 'LinkedIn → “Settings & Privacy” → “Data privacy” → “Get a copy of your data”.')}</li>
                      <li>{T('Wähle Posts, Positionen, Ausbildung, Skills und Profil (oder das komplette Archiv) und fordere es an.', 'Select posts, positions, education, skills and profile (or the full archive) and request it.')}</li>
                      <li>{T('LinkedIn schickt dir einen Download-Link per E-Mail (nach einigen Minuten bis zu einem Tag).', 'LinkedIn emails you a download link (a few minutes up to a day).')}</li>
                      <li>{T('Lade die ZIP-Datei hier hoch. Wir lesen nur Profil und Posts, keine Kontakte oder Nachrichten.', 'Upload the ZIP here. We only read profile and posts, not contacts or messages.')}</li>
                    </ol>
                    <p className="mt-1 text-[#6B7C90]">{T('Die Menünamen bei LinkedIn können abweichen.', 'Menu names on LinkedIn may differ.')}</p>
                  </div>
                )}
              </div>

              <div className="mt-5">
                <p className="text-sm text-white mb-1">{T('Möchtest du uns noch von dir erzählen?', 'Anything else you would like to tell us about yourself?')}</p>
                <Textarea value={pbAbout} onChange={e => { setPbAbout(e.target.value); setPbExtract(null); }} rows={4} placeholder={T('z. B. was dich antreibt, wofür du stehst, was du erreichen möchtest, wovon du überzeugt bist, worüber du lieber nicht sprichst …', 'e.g. what drives you, what you stand for, what you want to achieve, what you believe in, what you prefer not to talk about …')} className="bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D] resize-none" data-testid="pb-about" />
              </div>

              {pbExtract && (
                <div className="mt-4 rounded-lg border border-[#00D9C8]/40 bg-[#00D9C8]/5 p-3.5" data-testid="pb-extract-summary">
                  <p className="text-xs text-[#00D9C8] mb-1 flex items-center gap-1"><Check className="w-3.5 h-3.5" />{T('Das haben wir gefunden', 'What we found')}</p>
                  <p className="text-sm text-white">{[pbExtract.job, pbExtract.company].filter(Boolean).join(' · ') || T('Position nicht erkannt', 'Position not recognised')}</p>
                  <p className="text-xs text-[#A0AEC0] mt-1">
                    {[
                      pbExtract.career?.length ? T(`${pbExtract.career.length} Stationen`, `${pbExtract.career.length} positions`) : null,
                      pbExtract.education?.length ? T(`${pbExtract.education.length} Ausbildungen`, `${pbExtract.education.length} education entries`) : null,
                      pbExtract.skills?.length ? T(`${pbExtract.skills.length} Skills`, `${pbExtract.skills.length} skills`) : null,
                      pbExtract.achievements?.length ? T(`${pbExtract.achievements.length} Erfolge`, `${pbExtract.achievements.length} achievements`) : null,
                    ].filter(Boolean).join(' · ')}
                  </p>
                  {pbPosts.length > 0 && (
                    <p className="text-xs text-[#00D9C8] mt-1.5" data-testid="pb-extract-posts">{T(`${pbPosts.length} eigene Posts gefunden. Sie dienen als Vorbild für deinen Stil.`, `${pbPosts.length} of your own posts found. They serve as style examples.`)}</p>
                  )}
                  {pbExtract.roles?.length >= 2 && (
                    <p className="text-xs text-[#00D9C8] mt-1.5" data-testid="pb-extract-roles">{T(`${pbExtract.roles.length} parallele Rollen erkannt: `, `${pbExtract.roles.length} parallel roles found: `)}{pbExtract.roles.map(r => [r.job, r.company].filter(Boolean).join(' @ ')).join(' · ')}</p>
                  )}
                  {pbExtract.skills?.length > 0 && <p className="text-xs text-[#6B7C90] mt-1.5 truncate">{pbExtract.skills.slice(0, 6).join(', ')}</p>}
                </div>
              )}

              {pbError && <p className="text-red-400 text-xs mt-3" data-testid="pb-error">{pbError}</p>}
              <div className="mt-5">
                {pbExtract ? (
                  <SubmitBtn onClick={openWorkStep}>{T('Weiter', 'Continue')}</SubmitBtn>
                ) : (
                  <SubmitBtn loading={extracting} disabled={!cvFile && pbAbout.trim().length < 10} onClick={runExtract}>
                    {extracting ? T('Wird gelesen …', 'Reading …') : T('Auslesen & weiter', 'Read & continue')}
                  </SubmitBtn>
                )}
              </div>
            </motion.div>
          )}

          {/* ═══ Person 3/3: Arbeitsumfeld (pro Beschäftigung beliebig viele Quellen) ═══ */}
          {step === 'person_work' && (
            <motion.div key="pwork" {...anim}>
              <BackBtn lang={lang} onClick={() => { setStep('person_cv'); setPbError(''); }} />
              <PersonStepper current={3} lang={lang} />
              <h2 className="text-xl font-semibold text-white mb-1">{multiRole ? T('Deine Unternehmen', 'Your companies') : T('Erzähl uns über dein Unternehmen', 'Tell us about your company')}</h2>
              <p className="text-sm text-[#A0AEC0] mb-4">{multiRole
                ? T('Wir haben mehrere Beschäftigungen erkannt. Ergänze zu jeder, was du hast: Website(s), Projekte oder Dokumente. Alles ist optional und kombinierbar.', 'We found several jobs. For each one, add what you have: websites, projects or documents. All optional and combinable.')
                : T('Website(s), Projekte oder Dokumente, so viel du hast. Alles ist optional und kombinierbar. Der Fokus bleibt auf dir.', 'Website(s), projects or documents, as much as you have. All optional and combinable. The focus stays on you.')}</p>

              <div className="space-y-3" data-testid="pb-cards">
                {cardRoles.map((r, i) => {
                  const c = card(i);
                  const title = multiRole ? ([r.job, r.company].filter(Boolean).join(' @ ') || `${T('Beschäftigung', 'Job')} ${i + 1}`) : (r.company || T('Dein Unternehmen', 'Your company'));
                  return (
                    <div key={i} className="rounded-xl border border-[#2A3B52] bg-[#0F1D32] p-3.5 space-y-3" data-testid={`pb-card-${i}`}>
                      <p className="text-sm font-medium text-white">{title}</p>

                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-[#6B7C90] mb-1.5">{T('Website(s)', 'Website(s)')}</p>
                        {c.websites.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {c.websites.map(w => (
                              <span key={w} className="inline-flex items-center gap-1 rounded-full bg-[#1A2B42] px-2.5 py-1 text-xs text-white">
                                {w}<button type="button" onClick={() => updCard(i, { websites: c.websites.filter(x => x !== w) })} className="text-[#5A6C7D] hover:text-red-400" aria-label="Remove"><X className="w-3 h-3" /></button>
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Input value={c.siteInput} onChange={e => updCard(i, { siteInput: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSite(i); } }} placeholder={T('www.firma.de', 'www.company.com')} className="bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D] h-9" data-testid={`pb-site-input-${i}`} />
                          <Button type="button" onClick={() => addSite(i)} disabled={!c.siteInput.trim()} className="h-9 bg-[#1A2B42] hover:bg-[#2A3B52] text-white" data-testid={`pb-site-add-${i}`}><Plus className="w-4 h-4" /></Button>
                        </div>
                      </div>

                      {companyProjects.length > 0 && (
                        <div data-testid={`pb-projects-${i}`}>
                          <p className="text-[11px] uppercase tracking-wide text-[#6B7C90] mb-1.5">{T('Aus bestehendem Projekt', 'From an existing project')}</p>
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {companyProjects.map(p => {
                              const on = c.projectIds.includes(p.id);
                              return (
                                <button type="button" key={p.id} onClick={() => updCard(i, { projectIds: on ? c.projectIds.filter(x => x !== p.id) : [...c.projectIds, p.id] })} className={`w-full flex items-center gap-2 rounded-md border px-3 py-1.5 text-left transition-colors ${on ? 'border-[#00D9C8] bg-[#00D9C8]/10' : 'border-[#2A3B52] hover:border-[#00D9C8]/40'}`} data-testid={`pb-project-${i}-${p.id}`}>
                                  <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${on ? 'bg-[#00D9C8] border-[#00D9C8]' : 'border-[#5A6C7D]'}`}>{on && <Check className="w-3 h-3 text-[#0A1628]" />}</span>
                                  <span className="min-w-0 text-sm text-white truncate">{p.name}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-[#6B7C90] mb-1.5">{T('Dokumente', 'Documents')}</p>
                        {c.files.map((f, fi) => (
                          <div key={`${f.name}-${fi}`} className="flex items-center gap-2 rounded-md bg-[#1A2B42] px-3 py-1.5 mb-1.5" data-testid={`pb-docfile-${i}-${fi}`}>
                            <FileText className="w-4 h-4 text-[#00D9C8] shrink-0" /><span className="flex-1 text-sm text-white truncate">{f.name}</span>
                            <button type="button" onClick={() => updCard(i, { files: c.files.filter((_, idx) => idx !== fi) })} className="text-[#5A6C7D] hover:text-red-400" aria-label="Remove"><X className="w-4 h-4" /></button>
                          </div>
                        ))}
                        {importable.length > 0 && (
                          <div className="max-h-28 overflow-y-auto space-y-1 mb-1.5" data-testid={`pb-docs-existing-${i}`}>
                            {importable.map(d => {
                              const on = c.docIds.includes(d.id);
                              return (
                                <button type="button" key={d.id} onClick={() => updCard(i, { docIds: on ? c.docIds.filter(x => x !== d.id) : [...c.docIds, d.id] })} className={`w-full flex items-center gap-2 rounded-md border px-3 py-1.5 text-left transition-colors ${on ? 'border-[#00D9C8] bg-[#00D9C8]/10' : 'border-[#2A3B52] hover:border-[#00D9C8]/40'}`}>
                                  <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${on ? 'bg-[#00D9C8] border-[#00D9C8]' : 'border-[#5A6C7D]'}`}>{on && <Check className="w-3 h-3 text-[#0A1628]" />}</span>
                                  <span className="min-w-0 text-xs text-white truncate">{d.title} <span className="text-[#6B7C90]">· {d.project_name}</span></span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                        <label className="inline-flex items-center gap-2 rounded-md border border-dashed border-[#2A3B52] hover:border-[#00D9C8]/50 px-3 py-1.5 cursor-pointer text-xs text-[#A0AEC0] hover:text-white transition-colors">
                          <Upload className="w-4 h-4" />{T('Dokument hochladen (PDF, DOCX, TXT, MD)', 'Upload a document (PDF, DOCX, TXT, MD)')}
                          <input type="file" multiple className="sr-only" accept=".pdf,.docx,.txt,.md" onChange={e => { addCardFiles(i, e.target.files); e.target.value = ''; }} data-testid={`pb-docs-input-${i}`} />
                        </label>
                      </div>

                      <Textarea value={c.about} onChange={e => updCard(i, { about: e.target.value })} rows={2} placeholder={T('In deinen Worten (optional): Abteilung, Aufgaben, für wen ihr arbeitet …', 'In your own words (optional): department, tasks, who you work for …')} className="bg-[#1A2B42] border-[#1A2B42] text-white placeholder:text-[#5A6C7D] resize-none" data-testid={`pb-card-about-${i}`} />
                    </div>
                  );
                })}
              </div>

              {pbError && <p className="text-red-400 text-xs mt-3" data-testid="pb-error">{pbError}</p>}
              <div className="mt-5 space-y-2">
                <SubmitBtn loading={creating} onClick={() => createPersonProject(false)}>
                  {creating ? T('Quellen werden gelesen …', 'Reading sources …') : T('Profil erstellen', 'Create profile')}
                </SubmitBtn>
                <button type="button" disabled={creating} onClick={() => createPersonProject(true)} className="w-full text-sm text-[#5A6C7D] hover:text-white" data-testid="pb-skip-work">{T('Überspringen, später ergänzen', 'Skip, add later')}</button>
              </div>
            </motion.div>
          )}

          {/* ═══ Person: Profil wird gebaut (ohne Eingaben) ═══ */}
          {step === 'person_finalize' && createdPerson && (
            <motion.div key="pfinal" {...anim}>
              <StepPersonFinalize
                projectId={createdPerson.id}
                onComplete={() => { toast.success(T(`Projekt "${createdPerson.name}" ist bereit`, `Project "${createdPerson.name}" is ready`)); onComplete(createdPerson); }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const BackBtn = ({ onClick, lang }) => (
  <button onClick={onClick} className="text-[#5A6C7D] hover:text-white text-sm flex items-center gap-1 mb-4" data-testid="back-btn"><ArrowLeft className="w-4 h-4" /> {lang === 'de' ? 'Zurück' : 'Back'}</button>
);
const PersonStepper = ({ current, lang }) => {
  const steps = lang === 'de' ? ['Du', 'Lebenslauf', 'Unternehmen'] : ['You', 'CV', 'Company'];
  return (
    <div className="flex items-center gap-2 mb-4" data-testid="person-stepper">
      {steps.map((label, i) => (
        <div key={label} className="flex items-center gap-2">
          <span className={`w-5 h-5 rounded-full text-[11px] flex items-center justify-center ${i + 1 < current ? 'bg-[#00D9C8] text-[#0A1628]' : i + 1 === current ? 'border border-[#00D9C8] text-[#00D9C8]' : 'border border-[#2A3B52] text-[#5A6C7D]'}`}>
            {i + 1 < current ? <Check className="w-3 h-3" /> : i + 1}
          </span>
          <span className={`text-xs ${i + 1 === current ? 'text-white' : 'text-[#6B7C90]'}`}>{label}</span>
          {i < steps.length - 1 && <span className="w-6 h-px bg-[#2A3B52]" />}
        </div>
      ))}
    </div>
  );
};
const StepBadge = ({ n, total }) => (
  <p className="text-xs text-[#5A6C7D] mb-3" data-testid="step-badge">Schritt {n} von {total}</p>
);
const SubmitBtn = ({ children, loading, disabled, onClick, type }) => (
  <Button type={type || 'button'} onClick={onClick} disabled={disabled || loading} className="w-full bg-[#00D9C8] hover:bg-[#00C4B4] text-[#0A1628] font-medium" data-testid="step-submit-btn">
    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
    {children}
  </Button>
);
